import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription, forkJoin, of, interval } from 'rxjs';
import { finalize, catchError, startWith, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import {
    MonitoringService,
    UserMonitoringService,
    ActuatorHealthResponse,
    MemoryStatsResponse,
    HikariStatsResponse,
    CpuStatsResponse,
    UptimeResponse,
    Error5xxResponse
} from '../services/monitoring.service';
import { RoleAccessService } from '../auth/role-access.service';
import { AppFeature } from '../auth/features.enum';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { UserService } from '../pages/service/user.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Router } from '@angular/router';

@Component({
    selector: 'app-monitoring',
    standalone: true,
    imports: [CommonModule, ProgressSpinnerModule],
    templateUrl: './monitoring.component.html',
    styleUrl: './monitoring.component.scss'
})
export class MonitoringComponent implements OnInit, OnDestroy {
    // Order management metrics
    health: ActuatorHealthResponse | null = null;
    memory: MemoryStatsResponse | null = null;
    hikari: HikariStatsResponse | null = null;
    cpu: CpuStatsResponse | null = null;
    uptime: UptimeResponse | null = null;
    error5xx: Error5xxResponse | null = null;

    // User management (auth/UI) metrics
    uiHealth: ActuatorHealthResponse | null = null;
    uiMemory: MemoryStatsResponse | null = null;
    uiHikari: HikariStatsResponse | null = null;
    uiCpu: CpuStatsResponse | null = null;
    uiUptime: UptimeResponse | null = null;
    uiError5xx: Error5xxResponse | null = null;
    loading = true;
    error: string | null = null;
    refreshingOrder = false;
    refreshingUser = false;
    verifiedUsers: any[] = [];
    verifiedUsersError: string | null = null;
    loadingVerifiedUsers = false;
    refreshingVerifiedUsers = false;
    private sub = new Subscription();
    canAccess = false;
    readonly divisionOrder = ['100', '300', '400'];
    readonly maxUsersToShow = 500;
    readonly divisionLabels: Record<string, string> = {
        '100': 'Germany',
        '300': 'France',
        '400': 'Netherlands'
    };

    constructor(
        private monitoringService: MonitoringService,
        private userMonitoringService: UserMonitoringService,
        private userService: UserService,
        private roleAccessService: RoleAccessService,
        private inMemoryStorage: InMemoryStorageService,
        private router: Router
    ) {}

    goBack(): void {
        this.router.navigate(['/Orders1']);
    }

    ngOnInit(): void {
        // Access to this page is controlled at UI level by the sidebar menu:
        // Observability item is only rendered when the user has admin_group.
        const pollMs = 15000;

        // Order management metrics (poll only while this component is active)
        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.monitoringService.getHealth())
                )
                .subscribe({
                    next: (h) => {
                        this.health = h;
                        this.loading = false;
                        this.error = null;
                    },
                    error: (err: HttpErrorResponse) => {
                        this.loading = false;
                        this.error = this.getMonitoringErrorMessage(err);
                    }
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.monitoringService.getMemory())
                )
                .subscribe({
                    next: (m) => (this.memory = m),
                    error: () => (this.memory = null)
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.monitoringService.getHikari())
                )
                .subscribe({
                    next: (h) => (this.hikari = h),
                    error: () => (this.hikari = null)
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.monitoringService.getCpu())
                )
                .subscribe({
                    next: (c) => (this.cpu = c),
                    error: () => (this.cpu = null)
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.monitoringService.getUptime())
                )
                .subscribe({
                    next: (u) => (this.uptime = u),
                    error: () => (this.uptime = null)
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.monitoringService.getError5xx())
                )
                .subscribe({
                    next: (e) => (this.error5xx = e),
                    error: () => (this.error5xx = null)
                })
        );

        // User management (auth/UI) metrics (poll only while this component is active)
        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.userMonitoringService.getHealth())
                )
                .subscribe({
                    next: (h) => {
                        this.uiHealth = h;
                        this.loading = false;
                    },
                    error: () => {
                        this.uiHealth = null;
                    }
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.userMonitoringService.getMemory())
                )
                .subscribe({
                    next: (m) => (this.uiMemory = m),
                    error: () => (this.uiMemory = null)
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.userMonitoringService.getHikari())
                )
                .subscribe({
                    next: (h) => (this.uiHikari = h),
                    error: () => (this.uiHikari = null)
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.userMonitoringService.getCpu())
                )
                .subscribe({
                    next: (c) => (this.uiCpu = c),
                    error: () => (this.uiCpu = null)
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.userMonitoringService.getUptime())
                )
                .subscribe({
                    next: (u) => (this.uiUptime = u),
                    error: () => (this.uiUptime = null)
                })
        );

        this.sub.add(
            interval(pollMs)
                .pipe(
                    startWith(0),
                    switchMap(() => this.userMonitoringService.getError5xx())
                )
                .subscribe({
                    next: (e) => (this.uiError5xx = e),
                    error: () => (this.uiError5xx = null)
                })
        );

        this.refreshVerifiedUsers();
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    /** One-off refresh of all Order management metrics (calls APIs). */
    refreshOrderManagement(): void {
        if (this.refreshingOrder) return;
        this.refreshingOrder = true;
        const s = this.monitoringService;
        forkJoin({
            health: s.getHealth().pipe(catchError(() => of(null))),
            memory: s.getMemory().pipe(catchError(() => of(null))),
            hikari: s.getHikari().pipe(catchError(() => of(null))),
            cpu: s.getCpu().pipe(catchError(() => of(null))),
            uptime: s.getUptime().pipe(catchError(() => of(null))),
            error5xx: s.getError5xx().pipe(catchError(() => of(null)))
        })
            .pipe(
                finalize(() => (this.refreshingOrder = false))
            )
            .subscribe({
                next: (r) => {
                    this.health = r.health;
                    this.memory = r.memory;
                    this.hikari = r.hikari;
                    this.cpu = r.cpu;
                    this.uptime = r.uptime;
                    this.error5xx = r.error5xx;
                    this.error = r.health ? null : this.error;
                },
                error: (err: HttpErrorResponse) => (this.error = this.getMonitoringErrorMessage(err))
            });
    }

    /** One-off refresh of all User management metrics (calls APIs). */
    refreshUserManagement(): void {
        if (this.refreshingUser) return;
        this.refreshingUser = true;
        const s = this.userMonitoringService;
        forkJoin({
            health: s.getHealth().pipe(catchError(() => of(null))),
            memory: s.getMemory().pipe(catchError(() => of(null))),
            hikari: s.getHikari().pipe(catchError(() => of(null))),
            cpu: s.getCpu().pipe(catchError(() => of(null))),
            uptime: s.getUptime().pipe(catchError(() => of(null))),
            error5xx: s.getError5xx().pipe(catchError(() => of(null)))
        })
            .pipe(
                finalize(() => (this.refreshingUser = false))
            )
            .subscribe({
                next: (r) => {
                    this.uiHealth = r.health;
                    this.uiMemory = r.memory;
                    this.uiHikari = r.hikari;
                    this.uiCpu = r.cpu;
                    this.uiUptime = r.uptime;
                    this.uiError5xx = r.error5xx;
                }
            });
    }

    refreshVerifiedUsers(): void {
        if (this.refreshingVerifiedUsers) return;
        this.refreshingVerifiedUsers = true;
        this.loadingVerifiedUsers = true;
        this.userService
            .getVerifiedUsers()
            .pipe(
                finalize(() => {
                    this.refreshingVerifiedUsers = false;
                    this.loadingVerifiedUsers = false;
                })
            )
            .subscribe({
                next: (data) => {
                    this.verifiedUsers = Array.isArray(data) ? data : [];
                    this.verifiedUsersError = null;
                },
                error: (err) => {
                    this.verifiedUsersError = err?.message || 'Failed to load verified users';
                    this.verifiedUsers = [];
                }
            });
    }

    getUsersForDivision(division: string): any[] {
        return (this.verifiedUsers || []).filter((u) => String(u?.division || '') === division);
    }

    getDivisionLabel(division: string): string {
        return this.divisionLabels[division] || division;
    }

    /** Read roleGroup from localStorage or in-memory; normalize to string[] for access check. */
    private getUserRoles(): string[] {
        const fromStorage = localStorage.getItem('roleGroup');
        const fromMemory = this.inMemoryStorage.get('roleGroup');
        let raw: unknown = null;
        if (typeof fromStorage === 'string') {
            try {
                raw = JSON.parse(fromStorage);
            } catch {
                raw = fromStorage;
            }
        } else if (fromMemory !== null && fromMemory !== undefined) {
            raw = fromMemory;
        }
        if (Array.isArray(raw)) {
            return raw
                .map((r) => {
                    if (typeof r === 'string') return r;
                    const o = r as Record<string, unknown>;
                    return (o['name'] ?? o['groupName'] ?? o['roleName'] ?? '') as string;
                })
                .filter(Boolean);
        }
        if (raw != null && typeof raw === 'object') {
            const o = raw as Record<string, unknown>;
            const s = (o['name'] ?? o['groupName'] ?? o['roleName'] ?? '') as string;
            return s ? [s] : [];
        }
        return typeof raw === 'string' ? [raw] : [];
    }

    getComponentEntries(): [string, { status: string }][] {
        if (!this.health?.components) return [];
        return Object.entries(this.health.components).map(([name, comp]) => [
            name,
            { status: comp?.status ?? 'UNKNOWN' }
        ]);
    }

    getUiComponentEntries(): [string, { status: string }][] {
        if (!this.uiHealth?.components) return [];
        return Object.entries(this.uiHealth.components).map(([name, comp]) => [
            name,
            { status: comp?.status ?? 'UNKNOWN' }
        ]);
    }

    statusSeverity(status: string): 'success' | 'danger' | 'warning' | 'info' | 'secondary' {
        const s = (status || '').toUpperCase();
        if (s === 'UP' || s === 'HEALTHY') return 'success';
        if (s === 'DOWN' || s === 'UNHEALTHY') return 'danger';
        if (s === 'OUT_OF_SERVICE' || s === 'WARNING') return 'warning';
        return 'info';
    }

    memoryUtilizationPercent(): number {
        if (!this.memory?.health?.heapUtilization) return 0;
        const s = this.memory.health.heapUtilization.replace('%', '').trim();
        return parseFloat(s) || 0;
    }

    uiMemoryUtilizationPercent(): number {
        if (!this.uiMemory?.health?.heapUtilization) return 0;
        const s = this.uiMemory.health.heapUtilization.replace('%', '').trim();
        return parseFloat(s) || 0;
    }

    poolUtilizationPercent(): number {
        if (!this.hikari?.healthDetails?.utilizationPercentage) return 0;
        const s = this.hikari.healthDetails.utilizationPercentage.replace('%', '').trim();
        return parseFloat(s) || 0;
    }
    cpuUtilizationPercent(): number {
        if (!this.cpu?.health?.cpuUtilization) return 0;
        const s = this.cpu.health.cpuUtilization.replace('%', '').trim();
        return parseFloat(s) || 0;
    }

    uiCpuUtilizationPercent(): number {
        if (!this.uiCpu?.health?.cpuUtilization) return 0;
        const s = this.uiCpu.health.cpuUtilization.replace('%', '').trim();
        return parseFloat(s) || 0;
    }

    dbPendingCount(): number {
        return this.hikari?.threadsAwaitingConnection ?? 0;
    }

    uiPoolUtilizationPercent(): number {
        if (!this.uiHikari?.healthDetails?.utilizationPercentage) return 0;
        const s = this.uiHikari.healthDetails.utilizationPercentage.replace('%', '').trim();
        return parseFloat(s) || 0;
    }

    uiDbPendingCount(): number {
        return this.uiHikari?.threadsAwaitingConnection ?? 0;
    }

    private getMonitoringErrorMessage(err: HttpErrorResponse): string {
        const status = err?.status;
        const url = err?.url || 'monitoring endpoint';
        if (status === 0 || status === undefined) {
            return (
                'Monitoring endpoint unreachable. The server at this host may not expose /actuator and /internal. ' +
                'Ask DevOps to forward these paths to the backend, or set monitoringBaseUrl in environment to the backend URL that exposes them.'
            );
        }
        return err?.message || `Request failed (${status}) for ${url}`;
    }
}
